import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { mapApiRowsToEts1SessionConfig, syncEts1SessionFormControls } from "./ets1-session-form.utils";
import { buildEts1SessionColumnTree, Ets1SessionColumnVM, flattenEts1SessionTree } from "./ets1-session-layout.utils";
import { extractEts1SessionConfigurationsFromCandidate } from "./ets1-session-selection.utils";
import {
	enforceEts1SingleRootSelection,
	hasMissingRequiredEts1RadioSelection,
	hasMissingRequiredSessionText,
	markMissingRequiredEts1RadioSelection,
	missingRequiredSessionText,
	wireEts1RootSingleSelection,
} from "./ets1-session-validation.utils";

export type Ets35SessionSectionCode = "course_purpose" | "qualification" | "training_session";

export type Ets35SessionSectionVM = {
	code: Ets35SessionSectionCode;
	title: string;
	columns: Ets1SessionColumnVM[];
};

const SECTION_ORDER: Ets35SessionSectionCode[] = ["course_purpose", "qualification", "training_session"];

const SECTION_TITLES: Record<Ets35SessionSectionCode, string> = {
	course_purpose: "Course Purpose",
	qualification: "Qualification",
	training_session: "Training Session",
};

type SessionRow = {
	code: string;
	description: string;
	requiresText?: boolean;
	columnIndex?: number;
	displayOrder?: number;
	parentCode?: string | null;
	selectionMode?: string;
	session_conf_type?: { code?: string; description?: string } | null;
};

export function buildEts35SessionSections(rows: SessionRow[]): Ets35SessionSectionVM[] {
	return SECTION_ORDER.map((code) => {
		const group = rows.filter((row) => row.session_conf_type?.code === code);
		const title = group.find((row) => row.session_conf_type?.description)?.session_conf_type?.description;
		return {
			code,
			title: title || SECTION_TITLES[code],
			columns: buildEts1SessionColumnTree(mapApiRowsToEts1SessionConfig(group)),
		};
	}).filter((section) => section.columns.some((column) => column.items.length > 0));
}

export function ets35SectionsToSessionKeys(sections: Ets35SessionSectionVM[]) {
	return sections.map((section) => ({
		name: { code: section.code, description: section.title },
		details: flattenEts1SessionTree(section.columns).map((item) => ({
			code: item.code,
			description: item.description,
		})),
	}));
}

export function syncEts35AdminSessionControls(
	fb: UntypedFormBuilder,
	sessions: UntypedFormGroup,
	sessionRemarks: UntypedFormGroup,
	sections: Ets35SessionSectionVM[],
	subs: Array<{ unsubscribe: () => void }> = [],
): void {
	syncEts1SessionFormControls(
		fb,
		sessions,
		sessionRemarks,
		sections.flatMap((section) => section.columns),
		subs,
	);
	const coursePurpose = sections.find((section) => section.code === "course_purpose");
	if (coursePurpose) {
		enforceEts1SingleRootSelection(coursePurpose.columns, sessions, sessionRemarks);
		wireEts1RootSingleSelection(coursePurpose.columns, sessions, sessionRemarks, subs);
	}
}

export function syncEts35SectionSessionControls(
	fb: UntypedFormBuilder,
	section: Ets35SessionSectionVM,
	sessions: UntypedFormGroup,
	sessionRemarks: UntypedFormGroup,
	subs: Array<{ unsubscribe: () => void }> = [],
): void {
	syncEts1SessionFormControls(fb, sessions, sessionRemarks, section.columns, subs);
	if (section.code === "course_purpose") {
		enforceEts1SingleRootSelection(section.columns, sessions, sessionRemarks);
		wireEts1RootSingleSelection(section.columns, sessions, sessionRemarks, subs);
	}
}

export function enforceEts35CoursePurposeSingleSelection(
	sections: Ets35SessionSectionVM[],
	sessions: UntypedFormGroup,
	sessionRemarks?: UntypedFormGroup | null,
): void {
	const coursePurpose = sections.find((section) => section.code === "course_purpose");
	if (!coursePurpose) return;
	enforceEts1SingleRootSelection(coursePurpose.columns, sessions, sessionRemarks);
}

export function ets35TrainingColumns(sections: Ets35SessionSectionVM[]): Ets1SessionColumnVM[] {
	return sections.flatMap((section) => section.columns);
}

export function markEts35RadioParentsSelected(
	nested: Record<string, Record<string, boolean>>,
	sections: Ets35SessionSectionVM[],
): Record<string, Record<string, boolean>> {
	for (const section of sections) {
		const group = nested[section.code];
		if (!group) continue;
		for (const column of section.columns) {
			for (const item of column.items) {
				const selectedChildren = item.children.filter((child) => group[child.code]);
				if (selectedChildren.length > 0) {
					group[item.code] = true;
					for (const child of item.children) {
						group[child.code] = child.code === selectedChildren[0].code;
					}
				}
			}
		}
		if (section.code === "course_purpose") {
			let activeRootCode: string | null = null;
			for (const column of section.columns) {
				for (const item of column.items) {
					if (!group[item.code]) continue;
					if (!activeRootCode) {
						activeRootCode = item.code;
					} else {
						group[item.code] = false;
						for (const child of item.children) {
							group[child.code] = false;
						}
					}
				}
			}
		}
	}
	return nested;
}

export function ets35SelectedCodesBySection(
	candidate: UntypedFormGroup,
	sections: Ets35SessionSectionVM[],
): Record<string, string[]> {
	const selected: Record<string, string[]> = {};
	for (const section of sections) {
		selected[section.code] = extractEts1SessionConfigurationsFromCandidate(candidate, section.columns, section.code)
			.map((item) => item.code)
			.filter((code): code is string => !!code);
	}
	return selected;
}

export function ets35CandidatesMissingTrainingRules(
	candidates: Array<{ get: (path: string) => unknown }>,
	sections: Ets35SessionSectionVM[],
	markErrors: boolean,
): boolean {
	if (!sections.length) return false;

	let missing = false;
	for (const candidate of candidates) {
		const sessionDetails = candidate.get("sessionDetails") as UntypedFormGroup | null;
		const sessionRemarks = candidate.get("sessionRemarks") as UntypedFormGroup | null;
		if (!sessionDetails || !sessionRemarks) continue;

		for (const section of sections) {
			if (!section.columns.length) continue;
			const sessions = sessionDetails.get(section.code) as UntypedFormGroup | null;
			const remarks = sessionRemarks.get(section.code) as UntypedFormGroup | null;
			if (!sessions || !remarks) continue;

			const textMissing = markErrors
				? missingRequiredSessionText(sessions, remarks, section.columns)
				: hasMissingRequiredSessionText(sessions, remarks, section.columns);
			const radioMissing = markErrors
				? markMissingRequiredEts1RadioSelection(sessions, section.columns)
				: hasMissingRequiredEts1RadioSelection(sessions, section.columns);
			if (textMissing || radioMissing) missing = true;
		}
	}
	return missing;
}
